import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAutocomplete } from '@/hooks/useGoogleAutocomplete';
import { useGoogleMap } from '@/hooks/useGoogleMaps';

import { type GeoLocation, cleanupMapMarker, createMapMarker, geocodeLocation } from '../utils';
import styles from './gps-map.module.scss';

type GpsMapProps = {
  location: GeoLocation | null;
  setLocation: React.Dispatch<React.SetStateAction<GeoLocation | null>>;
  getInitialLocation: () => Promise<GeoLocation>;
};

export const GpsMap = ({ location, setLocation, getInitialLocation }: GpsMapProps) => {
  const { t } = useTranslation();

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const map = useGoogleMap(mapContainerRef, setLocation, getInitialLocation);
  useAutocomplete(searchInputRef, setLocation);

  const createMarker = useCallback(
    (loc: GeoLocation) => {
      if (!map) return null;

      const marker = createMapMarker(map, loc);

      marker.addListener('click', async () => {
        cleanupMapMarker(marker);

        const searchInput = searchInputRef.current;

        if (searchInput) {
          searchInput.value = '';
        }

        setLocation(null);
      });

      marker.addListener('dragend', async (markerEvent: google.maps.MapMouseEvent) => {
        const newPosition = markerEvent.latLng;

        const newLatitude = newPosition?.lat();
        const newLongitude = newPosition?.lng();

        if (newLatitude && newLongitude) {
          const geocoderResults = await geocodeLocation({
            location: { lat: newLatitude, lng: newLongitude },
          }).catch(() => {});

          const newAddress = geocoderResults?.[0]?.formatted_address;

          setLocation({
            latitude: newLatitude,
            longitude: newLongitude,
            address: newAddress,
          });
        }
      });

      return marker;
    },
    [map, setLocation],
  );

  useEffect(() => {
    let marker: google.maps.marker.AdvancedMarkerElement | google.maps.Marker | null = null;

    if (location && map) {
      map.panTo({
        lat: location.latitude,
        lng: location.longitude,
      });

      marker = createMarker(location);

      if (location.address && searchInputRef.current) {
        searchInputRef.current.value = location.address;
      }
    }

    return () => {
      cleanupMapMarker(marker);
    };
  }, [createMarker, location, map]);

  return (
    <div className={styles.mapContainer}>
      <div className={styles.map} ref={mapContainerRef} />
      <input
        className={styles.input}
        ref={searchInputRef}
        name="companyGpsAddress"
        placeholder={t('search')}
        type="text"
      />
    </div>
  );
};
